import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AlertCircle, ArrowRight, CheckCircle2, Coins, CreditCard, LockKeyhole, ShieldCheck, UserRound } from "lucide-react";
import { API_BASE_URL as API } from "../apiConfig";
import RetailMemberAuthPanel, { TravelAgencyAuthPanel } from "../components/retail/RetailMemberAuthPanel";
import PublicBrandMark from "../components/public/PublicBrandMark";
import PaymentDeadline, { usePaymentDeadline } from "../components/public/PaymentDeadline";

const formatJPYNumber = (amount) => Number(amount || 0).toLocaleString("ja-JP");
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("Turnstile unavailable"));
  if (window.turnstile) return Promise.resolve(window.turnstile);

  const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(window.turnstile), { once: true });
      existingScript.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(window.turnstile), { once: true });
    script.addEventListener("error", reject, { once: true });
    document.head.appendChild(script);
  });
}

const vehicleNames = {
  alphard: "Toyota Alphard",
  hiace: "Toyota HiAce",
  premium_hiace: "Premium HiAce",
  luxury_hiace: "Premium HiAce",
};

const languageOptions = [
  { code: "en", label: "EN" },
  { code: "ja", label: "日本語" },
  { code: "zh", label: "中国語" },
  { code: "zhHant", label: "繁体字" },
];

const charterAreaNames = {
  tokyo: { en: "Tokyo", ja: "東京", zh: "东京", zhHant: "東京" },
  haneda_airport: { en: "Haneda Airport", ja: "羽田空港", zh: "羽田机场", zhHant: "羽田機場" },
  narita_airport: { en: "Narita Airport", ja: "成田空港", zh: "成田机场", zhHant: "成田機場" },
  yokohama_port: { en: "Yokohama Port", ja: "横浜港", zh: "横滨港", zhHant: "橫濱港" },
  hakone: { en: "Hakone", ja: "箱根", zh: "箱根", zhHant: "箱根" },
  mt_fuji: { en: "Mt. Fuji", ja: "富士山", zh: "富士山", zhHant: "富士山" },
  kamakura: { en: "Kamakura", ja: "鎌倉", zh: "镰仓", zhHant: "鎌倉" },
  chiba: { en: "Chiba", ja: "千葉", zh: "千叶", zhHant: "千葉" },
  nikko: { en: "Nikko", ja: "日光", zh: "日光", zhHant: "日光" },
  kawagoe: { en: "Kawagoe", ja: "川越", zh: "川越", zhHant: "川越" },
  shizuoka: { en: "Shizuoka", ja: "静岡", zh: "静冈", zhHant: "靜岡" },
  odawara: { en: "Odawara", ja: "小田原", zh: "小田原", zhHant: "小田原" },
  mishima: { en: "Mishima", ja: "三島", zh: "三岛", zhHant: "三島" },
  karuizawa: { en: "Karuizawa", ja: "軽井沢", zh: "轻井泽", zhHant: "輕井澤" },
  kanazawa: { en: "Kanazawa", ja: "金沢", zh: "金泽", zhHant: "金澤" },
  nagoya: { en: "Nagoya", ja: "名古屋", zh: "名古屋", zhHant: "名古屋" },
  okinawa: { en: "Okinawa", ja: "沖縄", zh: "冲绳", zhHant: "沖繩" },
  osaka: { en: "Osaka", ja: "大阪", zh: "大阪", zhHant: "大阪" },
  kyoto: { en: "Kyoto", ja: "京都", zh: "京都", zhHant: "京都" },
  nara: { en: "Nara", ja: "奈良", zh: "奈良", zhHant: "奈良" },
  kobe: { en: "Kobe", ja: "神戸", zh: "神户", zhHant: "神戶" },
  kansai_airport: { en: "Kansai Airport", ja: "関西空港", zh: "关西机场", zhHant: "關西機場" },
  itami_airport: { en: "Itami Airport", ja: "伊丹空港", zh: "伊丹机场", zhHant: "伊丹機場" },
};

const paymentPageCopy = {
  en: {
    brand: "Shonan International",
    title: "Booking Payment",
    subtitle: "Please review your booking details and complete your payment.",
    bookingCount: (count) => `${count} booking${count === 1 ? "" : "s"}`,
    loading: "Loading payment details...",
    unavailable: "Payment page unavailable",
    unavailableMessage: "This payment page is not available.",
    invalidatedMessage: "This payment link is no longer valid because another link containing one or more of these bookings has already been paid.",
    paymentTotal: "Payment total",
    paymentType: "Payment Type",
    fullPayment: "Full Payment",
    reservationId: "Reservation ID",
    bookingConfirmation: "Booking confirmation",
        reviewBody: "Please review the invoice-style booking details below, then choose guest payment or sign in to pay as a member.",
    squareTrust: "Your payment is processed securely by Square. Shonan International does not store your card information.",
    customerSummary: "Customer summary",
    customerName: "Customer Name",
    vehicleType: "Vehicle Type",
    passengerCount: "Passenger Count",
    luggageCount: "Luggage Count",
    flightNumber: "Flight Number",
    childSeats: "Child Seats",
    infantSeats: "Infant Seats",
    olderChildSeats: "Child Seats (ages 3–8)",
    notRequested: "Not requested (0)",
    charterDuration: "Charter Duration",
    pickupTime: "Pickup Time",
    departureAddress: "Departure Address",
    endAddress: "End Address",
    itinerary: "Itinerary",
    phone: "Phone",
    booking: "Booking",
    pending: "Pending",
    totalAmount: "Total Amount",
    serviceDate: "Service Date",
    datePending: "Date pending",
    serviceType: "Service Type",
    pickupLocation: "Pickup Location",
    dropoffLocation: "Drop-off Location",
    charterDestination: "Charter Destination",
    toBeConfirmed: "To be confirmed",
    guestPayment: "Guest Payment",
    guestBody: "Pay now without creating an account.",
    continueGuest: "Continue as guest",
    memberPayment: "Member Payment",
        memberBody: "Sign in or sign up to use available points now and earn points after completion.",
        signedInAs: "Signed in as",
        pointsBalance: "Points balance",
        usePoints: "Use points",
        pointsRule: "1 point = ¥1. Up to 10% of this payment can be redeemed.",
        useMaximum: "Use maximum",
        pointsDiscount: "Points discount",
        amountAfterPoints: "Amount after points",
        payMember: "Pay as member",
        travelAgencyPayment: "Travel Agency Payment",
        travelAgencyBody: "Sign in with your travel agency account, then continue this payment by Square.",
        payTravelAgency: "Pay as travel agency",
        travelAgencyHome: "Open travel agency home",
        paymentCompleted: "Payment completed",
    openingSquare: "Opening Square...",
    squareNotReady: "Square payment is not ready yet. Our team will send you the secure payment link shortly.",
    startPaymentFailed: "Unable to start payment. Please try again.",
    paidTitle: "Thank you for your payment.",
    paidBody: "Your reservation has been confirmed. We will contact you with driver information before the service date.",
    saveReservationId: "Please save your Reservation ID",
    failedTitle: "Payment could not be completed.",
    failedBody: "Please try again or contact us for assistance.",
    privateCharter: "Private Charter",
    airportPickup: "Airport Pickup",
    airportDropoff: "Airport Drop-off",
    privateTransfer: "Private Transfer",
    charterTitle: (destination) => destination ? `Private charter to ${destination}` : "Private charter",
    transferTitle: "Private transfer",
  },
  ja: {
    brand: "湘南国際",
    title: "ご予約のお支払い",
    subtitle: "ご予約内容をご確認のうえ、お支払いください。",
    bookingCount: (count) => `${count}件の予約`,
    loading: "お支払い内容を読み込み中...",
    unavailable: "お支払いページを利用できません",
    unavailableMessage: "このお支払いページは現在利用できません。",
    invalidatedMessage: "この予約を含む別のお支払いリンクで決済が完了したため、このリンクはご利用いただけません。",
    paymentTotal: "お支払い合計",
    paymentType: "お支払い種別",
    fullPayment: "全額支払い",
    reservationId: "予約番号",
    bookingConfirmation: "予約内容確認",
    reviewBody: "下記の請求書形式の予約内容をご確認のうえ、ゲスト支払いまたは会員ログインでお支払いください。",
    squareTrust: "お支払いはSquareにより安全に処理されます。湘南国際はカード情報を保存しません。",
    customerSummary: "お客様情報",
    customerName: "お客様名",
    vehicleType: "車種",
    passengerCount: "乗車人数",
    luggageCount: "荷物数",
    flightNumber: "便名",
    childSeats: "チャイルドシート",
    infantSeats: "ベビーシート",
    olderChildSeats: "チャイルドシート（3～8歳）",
    notRequested: "不要（0台）",
    charterDuration: "貸切時間",
    pickupTime: "お迎え時間",
    departureAddress: "出発場所・詳細住所",
    endAddress: "終了場所・詳細住所",
    itinerary: "行程内容",
    phone: "電話番号",
    booking: "予約",
    pending: "確認中",
    totalAmount: "合計金額",
    serviceDate: "ご利用日時",
    datePending: "日付未定",
    serviceType: "サービス種別",
    pickupLocation: "乗車場所",
    dropoffLocation: "降車場所",
    charterDestination: "チャーター目的地",
    toBeConfirmed: "確認中",
    guestPayment: "ゲスト支払い",
    guestBody: "アカウント作成なしで今すぐお支払いできます。",
    continueGuest: "ゲストとして支払う",
    memberPayment: "会員支払い",
        memberBody: "ログインまたは登録すると、保有ポイントを今回のお支払いに利用でき、完了後にはポイントも獲得できます。",
        signedInAs: "ログイン中",
        pointsBalance: "保有ポイント",
        usePoints: "利用するポイント",
        pointsRule: "1ポイント＝¥1。今回のお支払い金額の10%まで利用できます。",
        useMaximum: "上限まで使う",
        pointsDiscount: "ポイント割引",
        amountAfterPoints: "ポイント適用後",
        payMember: "会員として支払う",
        travelAgencyPayment: "旅行会社アカウント支払い",
        travelAgencyBody: "旅行会社アカウントでログイン後、このお支払いをSquareで続行できます。",
        payTravelAgency: "旅行会社アカウントで支払う",
        travelAgencyHome: "旅行会社ホームへ",
        paymentCompleted: "支払い完了",
    openingSquare: "Squareを開いています...",
    squareNotReady: "Square決済リンクの準備がまだ完了していません。スタッフより安全な決済リンクをお送りします。",
    startPaymentFailed: "お支払いを開始できませんでした。もう一度お試しください。",
    paidTitle: "お支払いありがとうございます。",
    paidBody: "ご予約が確定しました。ご利用日前にドライバー情報をご連絡いたします。",
    saveReservationId: "予約番号を保存してください",
    failedTitle: "お支払いを完了できませんでした。",
    failedBody: "もう一度お試しいただくか、サポートまでお問い合わせください。",
    privateCharter: "貸切チャーター",
    airportPickup: "空港お迎え",
    airportDropoff: "空港お送り",
    privateTransfer: "片道送迎",
    charterTitle: (destination) => destination ? `${destination}への貸切チャーター` : "貸切チャーター",
    transferTitle: "片道送迎",
  },
  zh: {
    brand: "湘南国际",
    title: "预约付款",
    subtitle: "请确认预约信息后完成付款。",
    bookingCount: (count) => `${count} 个预约`,
    loading: "正在加载付款详情...",
    unavailable: "付款页面不可用",
    unavailableMessage: "此付款页面当前不可用。",
    invalidatedMessage: "包含其中一个或多个预约的其他链接已经付款，因此此付款链接已失效。",
    paymentTotal: "付款合计",
    paymentType: "付款类型",
    fullPayment: "全款支付",
    reservationId: "预约编号",
    bookingConfirmation: "预约内容确认",
    reviewBody: "请确认以下类似发票格式的预约内容，然后选择访客付款或登录会员后付款。",
    squareTrust: "您的付款由 Square 安全处理。Shonan International 不会保存您的银行卡信息。",
    customerSummary: "客户信息",
    customerName: "客户名",
    vehicleType: "车型",
    passengerCount: "乘客人数",
    luggageCount: "行李数量",
    flightNumber: "航班号",
    childSeats: "儿童座椅",
    infantSeats: "婴儿座椅",
    olderChildSeats: "儿童座椅（3–8岁）",
    notRequested: "不需要（0个）",
    charterDuration: "包车时长",
    pickupTime: "接客时间",
    departureAddress: "出发具体地址",
    endAddress: "结束具体地址",
    itinerary: "行程内容",
    phone: "电话",
    booking: "预约",
    pending: "待确认",
    totalAmount: "总金额",
    serviceDate: "服务日期",
    datePending: "日期待确认",
    serviceType: "服务类型",
    pickupLocation: "上车地点",
    dropoffLocation: "下车地点",
    charterDestination: "包车目的地",
    toBeConfirmed: "待确认",
    guestPayment: "访客付款",
    guestBody: "无需创建账户，可直接付款。",
    continueGuest: "以访客身份继续付款",
    memberPayment: "会员付款",
        memberBody: "登录或注册后，可使用账户积分抵扣本次付款，并在服务完成后获得积分。",
        signedInAs: "已登录",
        pointsBalance: "积分余额",
        usePoints: "使用积分",
        pointsRule: "1积分＝¥1，本次付款最多可抵扣10%。",
        useMaximum: "使用上限",
        pointsDiscount: "积分抵扣",
        amountAfterPoints: "积分抵扣后",
        payMember: "以会员身份付款",
        travelAgencyPayment: "旅行社账号付款",
        travelAgencyBody: "使用旅行社账号登录后，可以继续通过 Square 完成这次付款。",
        payTravelAgency: "以旅行社账号付款",
        travelAgencyHome: "进入旅行社主页",
        paymentCompleted: "付款已完成",
    openingSquare: "正在打开 Square...",
    squareNotReady: "Square 付款链接尚未准备好。工作人员会尽快发送安全付款链接给您。",
    startPaymentFailed: "无法开始付款，请重试。",
    paidTitle: "感谢您的付款。",
    paidBody: "您的预约已确认。我们会在服务日期前联系您并发送司机信息。",
    saveReservationId: "请保存您的预约编号",
    failedTitle: "付款未能完成。",
    failedBody: "请重试，或联系我们协助处理。",
    privateCharter: "包车服务",
    airportPickup: "机场接机",
    airportDropoff: "机场送机",
    privateTransfer: "单程接送",
    charterTitle: (destination) => destination ? `前往 ${destination} 的包车服务` : "包车服务",
    transferTitle: "单程接送",
  },
  zhHant: {
    brand: "湘南國際",
    title: "預約付款",
    subtitle: "請確認預約資訊後完成付款。",
    bookingCount: (count) => `${count} 個預約`,
    loading: "正在載入付款詳情...",
    unavailable: "付款頁面不可用",
    unavailableMessage: "此付款頁面目前不可用。",
    invalidatedMessage: "包含其中一個或多個預約的其他連結已經付款，因此此付款連結已失效。",
    paymentTotal: "付款合計",
    paymentType: "付款類型",
    fullPayment: "全款支付",
    reservationId: "預約編號",
    bookingConfirmation: "預約內容確認",
    reviewBody: "請確認以下類似發票格式的預約內容，然後選擇訪客付款或登入會員後付款。",
    squareTrust: "您的付款由 Square 安全處理。Shonan International 不會保存您的銀行卡資訊。",
    customerSummary: "客戶資訊",
    customerName: "客戶名",
    vehicleType: "車型",
    passengerCount: "乘客人數",
    luggageCount: "行李數量",
    flightNumber: "航班號",
    childSeats: "兒童座椅",
    infantSeats: "嬰兒座椅",
    olderChildSeats: "兒童座椅（3–8歲）",
    notRequested: "不需要（0個）",
    charterDuration: "包車時長",
    pickupTime: "接客時間",
    departureAddress: "出發具體地址",
    endAddress: "結束具體地址",
    itinerary: "行程內容",
    phone: "電話",
    booking: "預約",
    pending: "待確認",
    totalAmount: "總金額",
    serviceDate: "服務日期",
    datePending: "日期待確認",
    serviceType: "服務類型",
    pickupLocation: "上車地點",
    dropoffLocation: "下車地點",
    charterDestination: "包車目的地",
    toBeConfirmed: "待確認",
    guestPayment: "訪客付款",
    guestBody: "無需建立帳戶，可直接付款。",
    continueGuest: "以訪客身份繼續付款",
    memberPayment: "會員付款",
        memberBody: "登入或註冊後，可使用帳戶積分抵扣本次付款，並在服務完成後獲得積分。",
        signedInAs: "已登入",
        pointsBalance: "積分餘額",
        usePoints: "使用積分",
        pointsRule: "1積分＝¥1，本次付款最多可抵扣10%。",
        useMaximum: "使用上限",
        pointsDiscount: "積分抵扣",
        amountAfterPoints: "積分抵扣後",
        payMember: "以會員身份付款",
        travelAgencyPayment: "旅行社帳號付款",
        travelAgencyBody: "使用旅行社帳號登入後，可以繼續透過 Square 完成這次付款。",
        payTravelAgency: "以旅行社帳號付款",
        travelAgencyHome: "進入旅行社主頁",
        paymentCompleted: "付款已完成",
    openingSquare: "正在開啟 Square...",
    squareNotReady: "Square 付款連結尚未準備好。工作人員會盡快發送安全付款連結給您。",
    startPaymentFailed: "無法開始付款，請重試。",
    paidTitle: "感謝您的付款。",
    paidBody: "您的預約已確認。我們會在服務日期前聯絡您並發送司機資訊。",
    saveReservationId: "請保存您的預約編號",
    failedTitle: "付款未能完成。",
    failedBody: "請重試，或聯絡我們協助處理。",
    privateCharter: "包車服務",
    airportPickup: "機場接機",
    airportDropoff: "機場送機",
    privateTransfer: "單程接送",
    charterTitle: (destination) => destination ? `前往 ${destination} 的包車服務` : "包車服務",
    transferTitle: "單程接送",
  },
};

export default function PublicBookingPaymentPage() {
  const [lang, setLang] = useState(() => localStorage.getItem("public_booking_lang") || "en");
  const [paymentPage, setPaymentPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState("");
  const [error, setError] = useState("");
  const [member, setMember] = useState(null);
  const [memberToken, setMemberToken] = useState(() => localStorage.getItem("retail_member_token") || "");
  const [pointsToApply, setPointsToApply] = useState(0);
  const [travelAgencyUser, setTravelAgencyUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  });
  const [travelAgencyToken, setTravelAgencyToken] = useState(() => localStorage.getItem("token") || "");
  const [turnstileToken, setTurnstileToken] = useState("");

  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") || "", []);
  const common_customer = paymentPage?.common_customer || {};
  const orders = paymentPage?.orders || [];
  const showCommonCustomerSummary = orders.length === 1 && Object.keys(common_customer).length > 0;
  const total_amount_jpy = paymentPage?.total_amount_jpy || 0;
  const original_total_amount_jpy = paymentPage?.original_total_amount_jpy || total_amount_jpy;
  const alreadyAppliedPoints = Number(paymentPage?.retail_points_applied || 0);
  const availablePoints = Number(member?.points_balance || 0);
  const maxPointsToApply = Math.min(availablePoints, Math.floor(Number(original_total_amount_jpy || 0) * 0.1));
  const normalizedPointsToApply = Math.min(Math.max(0, Number(pointsToApply || 0)), maxPointsToApply);
  const displayedPaymentTotal = alreadyAppliedPoints > 0
    ? total_amount_jpy
    : Math.max(0, Number(total_amount_jpy || 0) - normalizedPointsToApply);
  const pageStatus = String(paymentPage?.status || "").toLowerCase();
  const squareStatus = String(paymentPage?.square_payment_status || "").toLowerCase();
  const isPaid = ["paid", "confirmed", "completed"].some((status) => pageStatus === status || squareStatus === status);
  const isPaymentFailed = ["failed", "payment_failed", "payment_link_failed", "canceled", "cancelled"].some(
    (status) => pageStatus === status || squareStatus === status
  );
  const turnstileRequired = Boolean(paymentPage?.turnstile_enabled && paymentPage?.turnstile_site_key);
  const paymentDeadline = usePaymentDeadline(paymentPage?.expires_at, paymentPage?.server_now);
  const paymentBlocked = paymentDeadline.expired || Boolean(turnstileRequired && !turnstileToken);
  const reservationIds = orders
    .map((order) => order.confirmation_code || order.order_no)
    .filter(Boolean);
  const copy = paymentPageCopy[lang] || paymentPageCopy.en;

  useEffect(() => {
    const previousHtmlBackground = document.documentElement.style.background;
    const previousHtmlOverflowX = document.documentElement.style.overflowX;
    const previousBodyBackground = document.body.style.background;
    const previousBodyOverflowX = document.body.style.overflowX;
    document.documentElement.style.background = "#000000";
    document.documentElement.style.overflowX = "hidden";
    document.body.style.background = "#000000";
    document.body.style.overflowX = "hidden";
    return () => {
      document.documentElement.style.background = previousHtmlBackground;
      document.documentElement.style.overflowX = previousHtmlOverflowX;
      document.body.style.background = previousBodyBackground;
      document.body.style.overflowX = previousBodyOverflowX;
    };
  }, []);

  const handleLanguageChange = (nextLang) => {
    setLang(nextLang);
    localStorage.setItem("public_booking_lang", nextLang);
  };

  const handleTurnstileVerify = useCallback((nextToken) => {
    setTurnstileToken(nextToken || "");
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
  }, []);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${API}/public-booking/payment-page`, { params: { token } });
        setPaymentPage(response.data?.payment_page || null);
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(
          detail?.error === "payment_page_invalidated"
            ? copy.invalidatedMessage
            : typeof detail === "string"
              ? detail
              : detail?.message || copy.unavailableMessage
        );
      } finally {
        setLoading(false);
      }
    };
    loadPage();
  }, [token, copy.invalidatedMessage, copy.unavailableMessage]);

  useEffect(() => {
    if (!memberToken || member) return;
    axios.get(`${API}/public-booking/member/me`, {
      headers: { "X-Retail-Member-Token": memberToken },
    }).then((response) => {
      setMember(response.data?.member || null);
    }).catch(() => {
      localStorage.removeItem("retail_member_token");
      setMemberToken("");
    });
  }, [member, memberToken]);

  const beginPayment = async (paymentMode) => {
    if (paymentDeadline.expired) return;
    setPaying(paymentMode);
    setError("");
    try {
      const response = await axios.post(`${API}/public-booking/payment-page/pay`, {
        token,
        payment_mode: paymentMode,
        retail_member_token: paymentMode === "member_payment" ? memberToken : undefined,
        retail_points_to_apply: paymentMode === "member_payment" ? normalizedPointsToApply : 0,
        travel_agency_token: paymentMode === "travel_agency_payment" ? travelAgencyToken : undefined,
        turnstile_token: turnstileToken,
      });
      const paymentUrl = response.data?.payment_url;
      if (paymentUrl) {
        window.location.href = paymentUrl;
        return;
      }
      setPaymentPage(response.data?.payment_page || paymentPage);
      setError(copy.squareNotReady);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setTurnstileToken("");
      setError(typeof detail === "string" ? detail : detail?.message || detail?.error || copy.startPaymentFailed);
    } finally {
      setPaying("");
    }
  };

  const handleMemberChange = (nextMember, nextToken) => {
    setMember(nextMember);
    setMemberToken(nextToken || localStorage.getItem("retail_member_token") || "");
    setPointsToApply(0);
  };

  const handleTravelAgencyChange = (nextUser, nextToken) => {
    setTravelAgencyUser(nextUser);
    setTravelAgencyToken(nextToken || localStorage.getItem("token") || "");
  };

  if (loading) {
    return (
      <main className="public-payment-page min-h-screen min-h-[100dvh] w-full max-w-full overflow-x-hidden bg-[#000000] px-4 py-10 text-white">
        <div className="mx-auto max-w-4xl rounded-[32px] border border-[#d8b769]/20 bg-[#0c0a07] p-8">{copy.loading}</div>
      </main>
    );
  }

  if (error && !paymentPage) {
    return (
      <main className="public-payment-page min-h-screen min-h-[100dvh] w-full max-w-full overflow-x-hidden bg-[#000000] px-4 py-10 text-white">
        <div className="mx-auto max-w-4xl rounded-[32px] border border-red-400/30 bg-[#0c0a07] p-8">
          <PaymentLanguageSwitcher lang={lang} onLanguageChange={handleLanguageChange} />
          <p className="mt-5 text-sm font-semibold uppercase tracking-[0.22em] text-[#d8b769]">{copy.brand}</p>
          <h1 className="mt-4 text-3xl font-semibold">{copy.unavailable}</h1>
          <p className="mt-3 text-white/65">{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="public-payment-page min-h-screen min-h-[100dvh] w-full max-w-full overflow-x-hidden bg-[#000000] px-4 py-6 text-white sm:py-10">
      <div className="mx-auto w-full min-w-0 max-w-6xl">
        {!isPaid && <PaymentDeadline expiresAt={paymentPage?.expires_at} remaining={paymentDeadline.remaining} language={lang} />}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <PublicBrandMark href="/book" />
            <div className="mt-5">
              <h1 className="text-3xl font-semibold tracking-normal sm:text-5xl">{copy.title}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/62 sm:text-base">{copy.subtitle}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PaymentLanguageSwitcher lang={lang} onLanguageChange={handleLanguageChange} />
            <div className="w-fit rounded-full border border-[#d8b769]/30 px-4 py-2 text-xs font-semibold text-[#f6d982]">
              {copy.bookingCount(orders.length)}
            </div>
          </div>
        </header>

        <section className="mt-6 grid min-w-0 gap-5 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="min-w-0 rounded-[34px] border border-[#d8b769]/25 bg-[#0c0a07] p-5 shadow-2xl shadow-[#d8b769]/10 sm:p-7">
            <div className="grid gap-5 border-b border-white/10 pb-5 md:grid-cols-[minmax(0,1fr)_minmax(260px,0.9fr)] md:items-stretch">
              <div className="rounded-[28px] border border-[#d8b769]/20 bg-[#d8b769]/[0.06] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/45">{copy.paymentTotal}</p>
                <MoneyAmount amount={displayedPaymentTotal} size="large" />
                <div className="mt-4 grid gap-2 text-sm">
                  <div className="flex items-center justify-between gap-3 rounded-2xl bg-black/25 px-3 py-2">
                    <span className="text-white/50">{copy.paymentType}</span>
                    <span className="font-semibold text-white">{copy.fullPayment}</span>
                  </div>
                  {reservationIds.length > 0 && (
                    <div className="flex flex-col gap-1 rounded-2xl bg-black/25 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-white/50">{copy.reservationId}</span>
                      <span className="break-words font-semibold text-white">{reservationIds.join(" / ")}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex h-full flex-col justify-between rounded-[28px] border border-white/10 bg-white/[0.035] p-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d8b769]">{copy.bookingConfirmation}</p>
                  <p className="mt-3 text-sm leading-6 text-white/62">
                    {copy.reviewBody}
                  </p>
                </div>
              </div>
            </div>

            {(isPaid || isPaymentFailed) && (
              <PaymentStatusNotice isPaid={isPaid} reservationIds={reservationIds} copy={copy} showTravelAgencyHome={Boolean(travelAgencyToken)} />
            )}

            {showCommonCustomerSummary && (
              <div className="mt-5 rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d8b769]">{copy.customerSummary}</p>
                <div className="mt-3 grid gap-3 text-sm text-white/80 sm:grid-cols-2">
                  {common_customer.customer_name && <Info label={copy.customerName} value={common_customer.customer_name} />}
                  {common_customer.vehicle_type && <Info label={copy.vehicleType} value={vehicleNames[common_customer.vehicle_type] || common_customer.vehicle_type} />}
                  {common_customer.passenger_count && <Info label={copy.passengerCount} value={common_customer.passenger_count} />}
                  {common_customer.luggage_count && <Info label={copy.luggageCount} value={common_customer.luggage_count} />}
                  {common_customer.phone && <Info label={copy.phone} value={common_customer.phone} />}
                </div>
              </div>
            )}

            <div className="mt-5 space-y-3">
              {orders.map((order, index) => (
                <article
                  key={order.id || index}
                  className="payment-page-order-card overflow-hidden rounded-3xl border border-white/10 bg-black/35 transition hover:border-[#d8b769]/35"
                >
                  <div className="flex flex-col gap-4 border-b border-white/10 p-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">{copy.booking} {index + 1}</p>
                      <h2 className="mt-2 break-words text-xl font-semibold leading-snug text-white">{getOrderTitle(order, copy, lang)}</h2>
                      <p className="mt-2 text-sm text-white/50">{copy.reservationId}: {order.confirmation_code || order.order_no || copy.pending}</p>
                    </div>
                    <div className="shrink-0 rounded-2xl border border-[#d8b769]/20 bg-[#d8b769]/10 px-4 py-3 sm:min-w-[160px]">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">{copy.totalAmount}</p>
                      <MoneyAmount amount={order.amount_jpy} size="small" align="left" />
                    </div>
                  </div>
                  <div className="grid gap-3 p-4 text-sm text-white/80 sm:grid-cols-2">
                    <Info label={copy.serviceDate} value={order.departure_date || copy.datePending} />
                    <Info label={copy.pickupTime} value={order.pickup_time || copy.toBeConfirmed} />
                    <Info label={copy.serviceType} value={formatServiceType(order, copy)} />
                    <Info label={isCharterOrder(order) ? copy.departureAddress : copy.pickupLocation} value={formatPaymentAddress(order.pickup_location, order.pickup_location_detail) || copy.toBeConfirmed} />
                    <Info label={isCharterOrder(order) ? copy.endAddress : copy.dropoffLocation} value={formatPaymentAddress(order.destination, order.dropoff_location_detail) || copy.toBeConfirmed} />
                    <Info label={copy.customerName} value={order.customer_name || copy.toBeConfirmed} />
                    {(!showCommonCustomerSummary || !common_customer.vehicle_type) && <Info label={copy.vehicleType} value={vehicleNames[order.vehicle_type] || order.vehicle_type || copy.toBeConfirmed} />}
                    {(!showCommonCustomerSummary || !common_customer.passenger_count) && <Info label={copy.passengerCount} value={order.passenger_count || copy.toBeConfirmed} />}
                    {(!showCommonCustomerSummary || !common_customer.luggage_count) && <Info label={copy.luggageCount} value={order.luggage_count ?? copy.toBeConfirmed} />}
                    <Info label={copy.flightNumber} value={order.flight_number || copy.toBeConfirmed} />
                    <Info label={copy.childSeats} value={order.child_seat_count === 0 ? copy.notRequested : (order.child_seat_count ?? copy.toBeConfirmed)} />
                    <Info label={copy.infantSeats} value={order.infant_seat_count || copy.notRequested} />
                    {order.child_seat_3_8_count > 0 && <Info label={copy.olderChildSeats} value={order.child_seat_3_8_count} />}
                    {isCharterOrder(order) && <Info label={copy.charterDuration} value={order.charter_duration || copy.toBeConfirmed} />}
                    {isCharterOrder(order) && <div className="min-w-0 sm:col-span-2"><Info label={copy.itinerary} value={order.trip_details || copy.toBeConfirmed} /></div>}
                  </div>
                </article>
              ))}
            </div>
          </div>

          <aside className="min-w-0 space-y-4">
            <section className="rounded-[30px] border border-[#d8b769]/25 bg-[#0c0a07] p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#d8b769] text-black">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{copy.guestPayment}</h2>
                  <p className="text-xs text-white/55">{copy.guestBody}</p>
                </div>
              </div>
              <SecurePaymentNote copy={copy} />
              {turnstileRequired && !isPaid && (
                <TurnstileVerification
                  siteKey={paymentPage.turnstile_site_key}
                  onVerify={handleTurnstileVerify}
                  onExpire={resetTurnstile}
                />
              )}
              <button
                type="button"
                onClick={() => beginPayment("guest_payment")}
                disabled={Boolean(paying) || isPaid || paymentBlocked}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#d8b769] px-4 py-3 text-sm font-bold text-black transition hover:brightness-110 disabled:opacity-50"
              >
                {isPaid ? copy.paymentCompleted : paying === "guest_payment" ? copy.openingSquare : copy.continueGuest}
                <ArrowRight className="h-4 w-4" />
              </button>
            </section>

            <section className="rounded-[30px] border border-white/10 bg-white/[0.04] p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[#d8b769]/30 text-[#d8b769]">
                  <UserRound className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{copy.memberPayment}</h2>
                  <p className="mt-1 text-xs leading-5 text-white/55">{copy.memberBody}</p>
                </div>
              </div>
              {member && (
                <div className="mt-3 flex items-center gap-2 rounded-2xl border border-emerald-300/20 bg-emerald-300/10 px-3 py-2 text-sm text-emerald-100">
                  <CheckCircle2 className="h-4 w-4" />
                  {copy.signedInAs} {member.email}
                </div>
              )}
              <div className="mt-4">
                <RetailMemberAuthPanel compact language={lang} paymentPageToken={token} onMemberChange={handleMemberChange} />
              </div>
              {member && (
                <div className="mt-4 rounded-3xl border border-[#d8b769]/25 bg-[#d8b769]/[0.07] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="flex items-center gap-2 text-sm font-semibold text-[#f6d982]">
                        <Coins className="h-4 w-4" />
                        {copy.pointsBalance}: {availablePoints.toLocaleString()}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-white/55">{copy.pointsRule}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPointsToApply(maxPointsToApply)}
                      disabled={maxPointsToApply <= 0 || alreadyAppliedPoints > 0}
                      className="shrink-0 rounded-full border border-[#d8b769]/35 px-3 py-1.5 text-xs font-semibold text-[#f6d982] disabled:opacity-40"
                    >
                      {copy.useMaximum}
                    </button>
                  </div>
                  <label className="mt-3 grid gap-2 text-xs font-semibold text-white/60">
                    {copy.usePoints}
                    <input
                      type="number"
                      min="0"
                      max={maxPointsToApply}
                      value={alreadyAppliedPoints || pointsToApply}
                      disabled={alreadyAppliedPoints > 0}
                      onChange={(event) => setPointsToApply(Math.min(maxPointsToApply, Math.max(0, Number(event.target.value || 0))))}
                      className="rounded-2xl border border-white/15 bg-white px-4 py-3 text-base text-black outline-none disabled:opacity-60"
                    />
                  </label>
                  <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                    <div className="rounded-2xl bg-black/25 px-3 py-2">
                      <span className="text-white/50">{copy.pointsDiscount}</span>
                      <p className="mt-1 font-semibold text-white">-¥{formatJPYNumber(alreadyAppliedPoints || normalizedPointsToApply)}</p>
                    </div>
                    <div className="rounded-2xl bg-black/25 px-3 py-2">
                      <span className="text-white/50">{copy.amountAfterPoints}</span>
                      <p className="mt-1 font-semibold text-[#f6d982]">¥{formatJPYNumber(displayedPaymentTotal)}</p>
                    </div>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => beginPayment("member_payment")}
                disabled={Boolean(paying) || !memberToken || isPaid || paymentBlocked}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-[#d8b769]/45 px-4 py-3 text-sm font-bold text-[#f6d982] transition hover:bg-[#d8b769]/10 disabled:opacity-40"
              >
                <LockKeyhole className="h-4 w-4" />
                {isPaid ? copy.paymentCompleted : paying === "member_payment" ? copy.openingSquare : copy.payMember}
              </button>
            </section>

            <section className="rounded-[30px] border border-white/10 bg-white/[0.04] p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[#d8b769]/30 text-[#d8b769]">
                  <UserRound className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{copy.travelAgencyPayment}</h2>
                  <p className="mt-1 text-xs leading-5 text-white/55">{copy.travelAgencyBody}</p>
                </div>
              </div>
              {travelAgencyUser && (
                <div className="mt-3 flex items-center gap-2 rounded-2xl border border-emerald-300/20 bg-emerald-300/10 px-3 py-2 text-sm text-emerald-100">
                  <CheckCircle2 className="h-4 w-4" />
                  {copy.signedInAs} {travelAgencyUser.name || travelAgencyUser.email}
                </div>
              )}
              <div className="mt-4">
                <TravelAgencyAuthPanel compact onTravelAgencyChange={handleTravelAgencyChange} />
              </div>
              <button
                type="button"
                onClick={() => beginPayment("travel_agency_payment")}
                disabled={Boolean(paying) || !travelAgencyToken || isPaid || paymentBlocked}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-[#d8b769]/45 px-4 py-3 text-sm font-bold text-[#f6d982] transition hover:bg-[#d8b769]/10 disabled:opacity-40"
              >
                <LockKeyhole className="h-4 w-4" />
                {isPaid ? copy.paymentCompleted : paying === "travel_agency_payment" ? copy.openingSquare : copy.payTravelAgency}
              </button>
            </section>

            {error && (
              <p className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm leading-6 text-red-100">{error}</p>
            )}
          </aside>
        </section>
      </div>
    </main>
  );
}

function isCharterOrder(order) {
  return String(order.service_type || "").toLowerCase() === "charter";
}

function formatServiceType(order, copy = paymentPageCopy.en) {
  if (isCharterOrder(order)) return copy.privateCharter;
  const subtype = String(order.transfer_subtype || "").toLowerCase();
  if (subtype === "airport_pickup" || subtype === "pickup") return copy.airportPickup;
  if (subtype === "airport_dropoff" || subtype === "dropoff") return copy.airportDropoff;
  return copy.privateTransfer;
}

function formatCharterArea(areaCode, lang = "en") {
  const normalizedCode = String(areaCode || "").trim();
  if (!normalizedCode) return "";
  const localizedName = charterAreaNames[normalizedCode];
  if (localizedName) return localizedName[lang] || localizedName.en;
  return normalizedCode
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getOrderTitle(order, copy = paymentPageCopy.en, lang = "en") {
  if (isCharterOrder(order)) {
    return copy.charterTitle(formatCharterArea(order.charter_area, lang));
  }
  return order.route || copy.transferTitle;
}

function PaymentLanguageSwitcher({ lang, onLanguageChange }) {
  return (
    <div
      className="payment-page-language-switcher flex w-full max-w-full flex-col gap-2 rounded-2xl border border-[#d8b769]/55 bg-[#120f08]/95 p-2 shadow-[0_18px_48px_rgba(0,0,0,0.42),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-xl sm:w-auto sm:flex-row sm:items-center sm:rounded-full"
      aria-label="Select payment page language"
      data-testid="payment-page-language-switcher"
    >
      <span className="whitespace-nowrap px-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#f6d982] sm:px-2">
        Language
      </span>
      <div className="grid grid-cols-2 gap-1 sm:flex sm:items-center" role="listbox" aria-label="Payment page language options">
        {languageOptions.map((option) => {
          const active = option.code === lang;
          return (
            <button
              key={option.code}
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => onLanguageChange(option.code)}
              className={`h-9 whitespace-nowrap rounded-full px-3 text-xs font-semibold transition ${
                active
                  ? "bg-[#d8b769] text-black shadow-[0_10px_26px_rgba(216,183,105,0.26)]"
                  : "border border-[#d8b769]/20 bg-black/35 text-white hover:border-[#d8b769]/50 hover:text-[#f6d982]"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PaymentStatusNotice({ isPaid, reservationIds, copy = paymentPageCopy.en, showTravelAgencyHome = false }) {
  if (isPaid) {
    return (
      <div className="mt-5 rounded-3xl border border-emerald-300/25 bg-emerald-300/10 p-4 text-emerald-50">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-200" />
          <div>
            <h2 className="text-lg font-semibold">{copy.paidTitle}</h2>
            <p className="mt-1 text-sm leading-6 text-emerald-50/80">
              {copy.paidBody}
            </p>
            {reservationIds.length > 0 && (
              <p className="mt-3 rounded-2xl bg-black/25 px-3 py-2 text-sm font-semibold text-white">
                {copy.saveReservationId}: {reservationIds.join(" / ")}
              </p>
            )}
            {showTravelAgencyHome && (
              <a href="/" className="mt-3 inline-flex items-center justify-center rounded-2xl border border-[#d8b769]/40 bg-[#d8b769]/10 px-4 py-2 text-sm font-bold text-[#f3d98b] transition hover:bg-[#d8b769] hover:text-black">
                {copy.travelAgencyHome}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 rounded-3xl border border-red-300/25 bg-red-400/10 p-4 text-red-50">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-200" />
        <div>
          <h2 className="text-lg font-semibold">{copy.failedTitle}</h2>
          <p className="mt-1 text-sm leading-6 text-red-50/80">{copy.failedBody}</p>
        </div>
      </div>
    </div>
  );
}

function SecurePaymentNote({ copy = paymentPageCopy.en }) {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-2xl border border-white/10 bg-black/25 p-3 text-xs leading-5 text-white/60">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#f6d982]" />
      <p>{copy.squareTrust}</p>
    </div>
  );
}

function TurnstileVerification({ siteKey, onVerify, onExpire }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!siteKey || !containerRef.current) return undefined;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current || widgetIdRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerify(token),
          "expired-callback": onExpire,
          "error-callback": onExpire,
        });
      })
      .catch(() => onExpire());

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, onVerify, onExpire]);

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-black/25 p-3">
      <div ref={containerRef} className="min-h-[65px]" />
    </div>
  );
}

function formatPaymentAddress(location, detail) {
  return [...new Set([location, detail].map(value => (value || "").trim()).filter(Boolean))].join("\n");
}

function Info({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/35">{label}</p>
      <p className="mt-1 whitespace-pre-wrap break-words font-medium text-white [overflow-wrap:anywhere]">{value}</p>
    </div>
  );
}

function MoneyAmount({ amount, size = "small", align = "left" }) {
  const currencyLabel = "JPY";
  const isLarge = size === "large";
  const alignment = align === "right" ? "justify-start sm:justify-end text-left sm:text-right" : "justify-start text-left";

  return (
    <div className={`mt-2 inline-flex ${alignment} max-w-full items-baseline gap-2 whitespace-nowrap text-[#f6d982]`}>
      <span className={`currencyLabel shrink-0 font-semibold tracking-[0.18em] ${isLarge ? "text-lg" : "text-xs"}`}>
        {currencyLabel}
      </span>
      <span className={`font-semibold tabular-nums leading-none ${isLarge ? "text-4xl sm:text-5xl" : "text-xl"}`}>
        {formatJPYNumber(amount)}
      </span>
    </div>
  );
}
